import { useMemo, useState } from 'react';
import {
  useAssetsPaginatedAll,
  useRealtimeAssetUpdates,
} from '@peritasai/repository';
import { AssetLocationSchema, RealtimeEventType } from '@peritasai/types';

function getLocationId(location: unknown): string | null {
  const parsed = AssetLocationSchema.safeParse(location);
  return parsed.success && parsed.data.id ? parsed.data.id : null;
}

const NO_TRACKED_ASSETS: string[] = [];

/** A physical asset currently located in the case's OR room. */
export interface RoomAsset {
  id: string;
  assetCatalogId: string;
  referenceNumber: string;
}

interface TrackedAssetLocation extends RoomAsset {
  locationId: string | null;
}

export interface AssetDetectionStatus {
  /** True while the baseline snapshot (assets already at the room on load) is loading. */
  isLoadingBaseline: boolean;
  /** Assets of a required catalog item, or tracked by id, currently located at roomLocationId. */
  assetsInRoom: RoomAsset[];
}

/**
 * Tracks which assets of the given required catalog items are currently
 * located in the case's OR room. Combines a one-time baseline snapshot of the
 * room (catches assets already there before this hook mounted) with a live
 * subscription (catches assets arriving, or leaving, while the page is open).
 * trackedAssetIds are specific assets (e.g. the selected cart) tracked
 * regardless of catalog.
 */
export function useAssetDetectionStatus(
  requiredCatalogItemIds: string[],
  roomLocationId: string | null,
  trackedAssetIds: string[] = NO_TRACKED_ASSETS
): AssetDetectionStatus {
  const enabled = !!roomLocationId && requiredCatalogItemIds.length > 0;
  const baseline = useAssetsPaginatedAll(
    { locationId: roomLocationId ?? undefined },
    100,
    enabled
  );
  const [liveByAssetId, setLiveByAssetId] = useState<
    Record<string, TrackedAssetLocation>
  >({});

  // Unfiltered by location: the server's locationId filter matches the new
  // location only, so it would hide assets leaving the room.
  useRealtimeAssetUpdates({
    eventTypes: [RealtimeEventType.ASSET_LOCATION_UPDATED],
    enabled,
    onEvent: (event) => {
      if (event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED) return;
      const { asset } = event.data;
      if (
        !requiredCatalogItemIds.includes(asset.assetCatalogId) &&
        !trackedAssetIds.includes(asset.id)
      ) {
        return;
      }

      setLiveByAssetId((prev) => ({
        ...prev,
        [asset.id]: {
          id: asset.id,
          assetCatalogId: asset.assetCatalogId,
          referenceNumber: asset.referenceNumber,
          locationId: getLocationId(asset.location),
        },
      }));
    },
  });

  const assetsInRoom = useMemo(() => {
    if (!roomLocationId) return [];

    const byAssetId = new Map<string, TrackedAssetLocation>();
    (baseline.data?.assets ?? []).forEach((asset) => {
      byAssetId.set(asset.id, {
        id: asset.id,
        assetCatalogId: asset.assetCatalogId,
        referenceNumber: asset.referenceNumber,
        locationId: getLocationId(asset.location),
      });
    });
    Object.values(liveByAssetId).forEach((asset) => {
      byAssetId.set(asset.id, asset);
    });

    const required = new Set(requiredCatalogItemIds);
    const tracked = new Set(trackedAssetIds);
    return Array.from(byAssetId.values())
      .filter(
        (asset) =>
          asset.locationId === roomLocationId &&
          (required.has(asset.assetCatalogId) || tracked.has(asset.id))
      )
      .map(({ id, assetCatalogId, referenceNumber }) => ({
        id,
        assetCatalogId,
        referenceNumber,
      }));
  }, [
    baseline.data,
    liveByAssetId,
    requiredCatalogItemIds,
    trackedAssetIds,
    roomLocationId,
  ]);

  return { isLoadingBaseline: baseline.isLoading, assetsInRoom };
}
